import { LegendProps, RangeSelectorProps } from "./types";
import { RWA_COLORS } from "~/styles/colors";
import { hexToRGB } from "~/utils/hexToRGB";

// // // //

export const DEFAULT_TIMESERIES_LEGEND_PROPS: LegendProps = {
    enabled: true,
    align: "right",
    layout: "vertical",
    verticalAlign: "top",
};

export const DEFAULT_RANGE_SELECTOR_PROPS: RangeSelectorProps = {
    enabled: false,
    inputEnabled: false, // Disable the date input boxes
    buttonPosition: {
        align: "right", // Align buttons to the right
        x: 0, // Adjust horizontal position
        y: 0, // Adjust vertical position
    },
};

/**
 * Visual style configuration for the Highcharts navigator panel.
 * All options map directly to Highcharts navigator API properties.
 * https://api.highcharts.com/highstock/navigator
 */
export const NAVIGATOR_STYLE = {
    // -- Panel --
    height: 40, // Height of the navigator panel in px
    margin: 10, // Gap between the main chart bottom and navigator top (negative moves it up)

    // -- Mask --
    // The shaded areas to the left/right of the selection window
    maskFill: hexToRGB(RWA_COLORS.blue[300], 0.15), // Fill color of the masked (unselected) regions
    maskInside: true, // true = shade outside the selection; false = shade inside

    // -- Outline --
    // The border drawn around the full navigator extent
    outlineColor: RWA_COLORS.blue[100], // Color of the outline border
    outlineWidth: 1, // Width of the outline border in px

    // -- Handles --
    // The draggable grips on each edge of the selection window
    handles: {
        backgroundColor: RWA_COLORS.blue[50], // Fill color of each handle
        borderColor: RWA_COLORS.blue[300], // Border color of each handle
        width: 8, // Width of each handle in px
        height: 18, // Height of each handle in px
    },

    // -- Mini-chart series --
    series: {
        color: RWA_COLORS.blue[300], // Line color of the mini-chart
        lineWidth: 1, // Line width of the mini-chart
        // Gradient fill beneath the mini-chart line
        fillColorStops: [
            [0, hexToRGB(RWA_COLORS.blue[500], 0.5)], // top of gradient
            [1, hexToRGB(RWA_COLORS.blue[500], 0.05)], // bottom of gradient
        ] as [number, string][],
    },
};

// // // //

export const TODAYS_DATE = new Date(new Date().setHours(0, 0, 0, 0)).getTime(); // Start of today

// // // //

/**
 * Returns a new Date object set to X days ago from today
 */
const getDaysAgo = (days: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    date.setMinutes(0, 0, 0);
    return date;
};

// More like 28 hours ago but w/e
// export const ONE_DAY_AGO = new Date(new Date().getTime() - 28 * 60 * 60 * 1000);

export const SEVEN_DAYS_AGO = getDaysAgo(7);

export const TWO_WEEKS_AGO = getDaysAgo(14);

export const THIRTY_DAYS_AGO = getDaysAgo(30);

export const SIXTY_DAYS_AGO = getDaysAgo(60);

export const NINETY_DAYS_AGO = getDaysAgo(90);

export const SIX_MONTHS_AGO = getDaysAgo(180);

export const THREE_QUARTERS_AGO = getDaysAgo(270);

export const ONE_YEAR_AGO = getDaysAgo(365);

export const THREE_YEARS_AGO = getDaysAgo(365 * 3);

// // // //

// Define VisualizationModes
export const TWO_YEARS_AGO = getDaysAgo(365 * 2).getTime(); // Approximately 24 months ago

export const FOUR_YEARS_AGO = getDaysAgo(365 * 4).getTime(); // Approximately 48 months ago

// export const NAVIGATOR_X_AXIS_ONE_YEAR_AGO: NavigatorXAxisState = {
//     min: Number(ONE_YEAR_AGO.getTime()),
//     max: Number(TODAYS_DATE),
// };

// export const NAVIGATOR_X_AXIS_SIX_MONTHS_AGO: NavigatorXAxisState = {
//     min: Number(SIX_MONTHS_AGO.getTime()),
//     max: Number(TODAYS_DATE),
// };
